import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Check, Info, ChevronDown, Layers } from 'lucide-react'
import { Drawer, DrawerFooter, Segmented, ChoiceChips, Textarea, InlineError } from '../../kit'
import { labPatientName, labPatientHN } from '../../lib/lab.js'
import {
  PRODUCTION_TYPES, RETAINER_TYPES, RETAINER_PRESETS, RETAINER_PATTERNS, patternColorCount,
  RETAINER_COLORS, colorByName, colorImg, presetImg, patternImg,
  RETAINER_STICKERS, STICKER_CATEGORIES, STICKER_SLOTS, stickerImg, stickerById,
} from '../../data/retainerCatalog.js'

// Interactive retainer / lab-detail builder (modal). Progressive disclosure that mirrors
// the clinic's workflow:
//   production_type → (retainer) → upper/lower → type (Clear ends) → preset (ends) OR
//   custom: pattern → colours (count set by pattern) → หุ้มลวด → note.
// Hands lab.production_type + the full lab.retainer{} back through onSave. Glitter still
// goes in the note.
//
// ⚠️ IT NO LONGER WRITES (2026-08-17). Both callers — the detail drawer's single edit mode
// and the create form — capture the config into their own state and send it with their own
// one save. So this button must NOT say บันทึก: a user who pressed it and then closed the
// drawer would have lost the design believing it was stored, and one who saw "ยังไม่บันทึก"
// afterwards would reasonably conclude the app was broken. It says ใช้ค่านี้, and the
// footer names where the real save is.
export default function RetainerBuilder({ row, onSave, onClose, submitLabel = 'ใช้ค่านี้', submitHint = 'ยังไม่บันทึกลงระบบ — กด บันทึก อีกครั้งที่หน้ารายละเอียด' }) {
  const ex = row?.retainer || {}
  const hasSide = ex.retainer_upper || ex.retainer_lower

  const [pt, setPt] = useState(row?.production_type || 'retainer')
  const [upper, setUpper] = useState(hasSide ? !!ex.retainer_upper : true)
  const [lower, setLower] = useState(hasSide ? !!ex.retainer_lower : true)
  const [type, setType] = useState(ex.retainer_type || 'Wraparound')
  const [usePreset, setUsePreset] = useState(!!ex.retainer_preset)
  const [preset, setPreset] = useState(ex.retainer_preset || '')
  const [pattern, setPattern] = useState(ex.retainer_pattern || '')
  const [colors, setColors] = useState(Array.isArray(ex.retainer_color) ? ex.retainer_color.filter(Boolean) : [])
  const rub = ex.retainer_rubber || {}
  const [rubUpper, setRubUpper] = useState(!!rub.upper)
  const [rubLower, setRubLower] = useState(!!rub.lower)
  const [rubUpperColor, setRubUpperColor] = useState(rub.upper_color || '')
  const [rubLowerColor, setRubLowerColor] = useState(rub.lower_color || '')
  const [stickers, setStickers] = useState(
    Array.isArray(ex.retainer_addon) ? ex.retainer_addon.filter((x) => typeof x === 'string') : [])
  const [stickerCat, setStickerCat] = useState(STICKER_CATEGORIES[0])
  const [hoverColor, setHoverColor] = useState(null)
  const [note, setNote] = useState(ex.retainer_note || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const isRetainer = pt === 'retainer'
  const isClear = type === 'Clear'
  const showCustom = isRetainer && !isClear && !usePreset
  const needColors = showCustom && pattern ? patternColorCount(pattern) : 0

  // Trim the colour list when the pattern (and so the required count) changes.
  useEffect(() => { if (pattern) setColors((cs) => cs.slice(0, patternColorCount(pattern))) }, [pattern])

  // CATALOGUE ONLY (Ram, 2026-09-07). The free-text "พิมพ์ชื่อสีเอง" box that used to sit under
  // the swatches is gone: a name the bench cannot match to a real material is not an
  // order, and a typo’d or invented colour reached the technician looking exactly as
  // valid as R01. Every colour now comes from RETAINER_COLORS, so `colorByName` always
  // resolves and the chip can show the photo and the code.
  // Legacy rows may still hold a free-typed name — the chip renders those with a blank
  // swatch so they stay visible and removable, they just can no longer be created.
  function addColor(name) {
    const n = String(name || '').trim()
    if (!n) return
    setColors((cs) => (cs.includes(n) || cs.length >= needColors ? cs : [...cs, n]))
  }
  const removeColor = (i) => setColors((cs) => cs.filter((_, idx) => idx !== i))

  // Stickers are a set, not an ordered list — tapping a picked one takes it off again.
  function toggleSticker(id) {
    setStickers((ss) => (ss.includes(id)
      ? ss.filter((x) => x !== id)
      : ss.length >= STICKER_SLOTS ? ss : [...ss, id]))
  }

  // WHY the save is off, not just THAT it is. The step headers already carry their own
  // counters ("เลือกครบ 3 สี (2/3)"), but they scroll out of view long before the footer,
  // so a disabled บันทึก at the bottom of a tall form looked broken rather than blocked.
  // One string, and `valid` is derived from it — the two cannot drift apart.
  const blockReason = useMemo(() => {
    if (!isRetainer) return ''
    if (!upper && !lower) return 'เลือกตำแหน่ง บน หรือ ล่าง ก่อน'
    if (isClear) return ''
    if (usePreset) return preset ? '' : 'เลือกลายสำเร็จรูป'
    if (!pattern) return 'เลือกลายสี (Pattern)'
    const need = patternColorCount(pattern)
    if (colors.length !== need) return `เลือกสีให้ครบ ${need} สี (ตอนนี้ ${colors.length})`
    if (rubUpper && !rubUpperColor) return 'เลือกสีหุ้มลวดบน'
    if (rubLower && !rubLowerColor) return 'เลือกสีหุ้มลวดล่าง'
    return ''
  }, [isRetainer, upper, lower, isClear, usePreset, preset, pattern, colors, rubUpper, rubUpperColor, rubLower, rubLowerColor])
  const valid = !blockReason

  // Unsaved-edit guard (kit Drawer `dirty`): a stray backdrop click or Esc asks
  // "ทิ้งการแก้ไข?" instead of silently throwing the design away.
  const signature = JSON.stringify([pt, upper, lower, type, usePreset, preset, pattern, colors, rubUpper, rubLower, rubUpperColor, rubLowerColor, stickers, note])
  const initialSig = useRef(signature)
  const dirty = signature !== initialSig.current

  async function save() {
    if (!valid || saving) return
    setSaving(true); setErr('')
    let retainer
    if (!isRetainer) {
      // ดี-aligner: preserve the existing retainer config, update only the note.
      retainer = {
        retainer_upper: !!ex.retainer_upper, retainer_lower: !!ex.retainer_lower,
        retainer_type: ex.retainer_type || '', retainer_preset: ex.retainer_preset || '',
        retainer_pattern: ex.retainer_pattern || '', retainer_color: Array.isArray(ex.retainer_color) ? ex.retainer_color : [],
        retainer_addon: Array.isArray(ex.retainer_addon) ? ex.retainer_addon : [],
        retainer_note: note || '',
        retainer_rubber: {
          upper: !!rub.upper, lower: !!rub.lower, upper_color: rub.upper_color || '', lower_color: rub.lower_color || '',
        },
      }
    } else {
      const presetVal = isClear ? '' : (usePreset ? preset : '')
      const custom = !isClear && !presetVal
      retainer = {
        retainer_upper: !!upper, retainer_lower: !!lower, retainer_type: type,
        retainer_preset: presetVal,
        retainer_pattern: custom ? (pattern || '') : '',
        retainer_color: custom ? colors.slice(0, patternColorCount(pattern)) : [],
        retainer_addon: custom ? stickers : [],
        retainer_note: note || '',
        retainer_rubber: (!isClear) ? {
          upper: !!rubUpper, lower: !!rubLower,
          upper_color: rubUpper ? (rubUpperColor || '') : '', lower_color: rubLower ? (rubLowerColor || '') : '',
        } : { upper: false, lower: false, upper_color: '', lower_color: '' },
      }
    }
    try {
      await onSave(row, { production_type: pt, retainer })
      onClose()
    } catch (e) {
      setErr(e.message || 'บันทึกไม่สำเร็จ'); setSaving(false)
    }
  }

  const summary = summarize({ pt, upper, lower, type, usePreset, preset, pattern, colors, stickers, isRetainer })
  return (
    <Drawer
      onClose={onClose}
      busy={saving}
      dirty={dirty}
      icon={Layers}
      title="แก้ไขรายละเอียด Lab"
      subtitle={`${labPatientName(row)}${labPatientHN(row) ? ` · HN ${labPatientHN(row)}` : ''}${row.id ? ` · Lab #${row.id}` : ''}`}
      footer={
        <DrawerFooter
          onCancel={onClose}
          onSave={save}
          saving={saving}
          canSave={valid}
          saveLabel={submitLabel}
          statusTone={blockReason ? 'warn' : 'muted'}
          status={blockReason || (
            <>
              <span className="block truncate">{summary}</span>
              {submitHint && <span className="block truncate text-[12px] text-amber-600">{submitHint}</span>}
            </>
          )}
        />
      }
    >
        <div className="space-y-5">
          {/* 1 · production type */}
          <Field label="ประเภทงาน" step={1}>
            <Segmented options={PRODUCTION_TYPES.map((p) => ({ value: p.value, label: p.label }))} value={pt} onChange={setPt} />
          </Field>

          {!isRetainer ? (
            <Field label="โน้ตถึง Lab">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="รายละเอียดงาน ดี-aligner…" />
            </Field>
          ) : (
            <>
              {/* 2 · upper / lower */}
              <Field label="ตำแหน่ง" step={2} hint="เลือกได้ทั้งบนและล่าง">
                <ChoiceChips multiple
                  options={[{ value: 'upper', label: 'บน (Upper)' }, { value: 'lower', label: 'ล่าง (Lower)' }]}
                  values={[upper && 'upper', lower && 'lower'].filter(Boolean)}
                  onChange={(set) => { setUpper(set.has('upper')); setLower(set.has('lower')) }} />
                {!upper && !lower && <ErrLine>เลือกอย่างน้อย 1 ตำแหน่ง</ErrLine>}
              </Field>

              {/* 3 · type */}
              <Field label="ชนิดรีเทนเนอร์" step={3}>
                <Segmented options={RETAINER_TYPES.map((t) => ({ value: t, label: t }))} value={type} onChange={setType} />
                {isClear && <Note><Info size={13} className="inline -mt-0.5 mr-1" />รีเทนเนอร์ใส (Clear) ไม่มีสี/ลาย — ระบุรายละเอียดเพิ่มเติมในโน้ต</Note>}
              </Field>

              {!isClear && (
                <>
                  {/* 4 · preset vs custom */}
                  <Field label="รูปแบบ" step={4}>
                    <Segmented options={[{ value: 'custom', label: 'กำหนดเอง' }, { value: 'preset', label: 'ลายสำเร็จรูป (Preset)' }]} value={usePreset ? 'preset' : 'custom'} onChange={(v) => setUsePreset(v === 'preset')} />
                  </Field>

                  {usePreset ? (
                    <Field label="เลือกลายสำเร็จรูป" hint="เลือกลายแล้วจบขั้นตอน">
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {RETAINER_PRESETS.map((p) => {
                          const sel = preset === p.name
                          return (
                            <button key={p.slug} type="button" onClick={() => setPreset(p.name)} title={p.name}
                              className={`relative rounded-lg overflow-hidden border-2 bg-white transition ${sel ? 'border-brand-500 ring-2 ring-brand-200' : 'border-slate-200 hover:border-slate-300'}`}>
                              <img src={presetImg(p.slug)} alt={p.name} loading="lazy" className="w-full aspect-square object-contain bg-slate-50" />
                              <div className="px-1 py-0.5 text-[12px] text-slate-600 truncate text-center border-t border-slate-100">{p.name}</div>
                              {sel && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-600 text-white flex items-center justify-center"><Check size={11} strokeWidth={3} /></span>}
                            </button>
                          )
                        })}
                      </div>
                    </Field>
                  ) : (
                    <>
                      {/* 5 · pattern */}
                      <Field label="ลายสี (Pattern)" step={5}>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {RETAINER_PATTERNS.map((p) => {
                            const sel = pattern === p.name
                            return (
                              <button key={p.slug} type="button" onClick={() => setPattern(p.name)} title={p.name}
                                className={`relative rounded-lg overflow-hidden border-2 bg-white text-left transition ${sel ? 'border-brand-500 ring-2 ring-brand-200' : 'border-slate-200 hover:border-slate-300'}`}>
                                <img src={patternImg(p.slug)} alt={p.name} loading="lazy" className="w-full aspect-square object-contain bg-slate-50" />
                                <div className="px-1.5 py-1 border-t border-slate-100">
                                  <div className="text-[12px] text-slate-700 leading-tight">{p.name}</div>
                                  <div className="text-[12px] text-slate-400">{p.code} · {p.colors} สี</div>
                                </div>
                                {sel && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-600 text-white flex items-center justify-center"><Check size={11} strokeWidth={3} /></span>}
                              </button>
                            )
                          })}
                        </div>
                      </Field>

                      {/* 6 · colours */}
                      {pattern && (
                        <Field label="เลือกสี" step={6} hint={`เลือกครบ ${needColors} สี (${colors.length}/${needColors})`}>
                          {colors.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2.5">
                              {colors.map((c, i) => {
                                const hit = colorByName(c)
                                return (
                                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 pl-1 pr-2 py-0.5 text-[12.5px]">
                                    <span className="text-[12px] text-slate-400 w-4 text-center">{i + 1}</span>
                                    {hit
                                      ? <img src={colorImg(hit.slug)} alt="" className="w-5 h-5 object-contain" />
                                      : <span className="w-5 h-5 rounded-full border border-slate-300 bg-slate-100" />}
                                    <span className="text-slate-700">{c}</span>
                                    {hit && <span className="text-[11.5px] text-slate-400">{hit.code}</span>}
                                    <button onClick={() => removeColor(i)} className="text-slate-400 hover:text-rose-600"><X size={12} /></button>
                                  </span>
                                )
                              })}
                            </div>
                          )}
                          <ColorPreview color={hoverColor} fallback="ชี้ที่สีเพื่อดูชื่อสี" />
                          <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                            {RETAINER_COLORS.map((c) => {
                              const picked = colors.includes(c.name)
                              return (
                                <ColorTile key={c.code} color={c} selected={picked}
                                  disabled={colors.length >= needColors && !picked}
                                  onClick={() => addColor(c.name)} onHover={setHoverColor} />
                              )
                            })}
                          </div>
                        </Field>
                      )}

                      {/* 7 · rubber (หุ้มลวด) */}
                      <Field label="หุ้มลวด (Rubber)" step={7} hint="เลือกได้ทั้งบน/ล่าง แล้วเลือกสี">
                        <div className="space-y-2">
                          <RubberRow label="บน (Upper)" on={rubUpper} onToggle={() => setRubUpper((v) => !v)} color={rubUpperColor} onColor={setRubUpperColor} />
                          <RubberRow label="ล่าง (Lower)" on={rubLower} onToggle={() => setRubLower((v) => !v)} color={rubLowerColor} onColor={setRubLowerColor} />
                        </div>
                      </Field>
                    </>
                  )}
                </>
              )}

              {/* 8 · stickers — same catalogue and 3-slot cap as the public designer */}
              {!isClear && !usePreset && (
                <Field label="สติกเกอร์" step={8} hint={`เลือกได้สูงสุด ${STICKER_SLOTS} ดวง (${stickers.length}/${STICKER_SLOTS}) · กลิตเตอร์ระบุในโน้ต`}>
                  {stickers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2.5">
                      {stickers.map((id) => {
                        const st = stickerById(id)
                        return (
                          <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 pl-1 pr-2 py-0.5 text-[12.5px]">
                            {st
                              ? <img src={stickerImg(id)} alt="" className="w-5 h-5 object-contain" />
                              : <span className="w-5 h-5 rounded-full bg-slate-200" />}
                            <span className="text-slate-700">{st?.name || id}</span>
                            <button onClick={() => toggleSticker(id)} className="text-slate-400 hover:text-rose-600"><X size={12} /></button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <ChoiceChips size="sm" value={stickerCat} onChange={setStickerCat}
                    options={STICKER_CATEGORIES.map((cat) => ({ value: cat, label: cat }))} />
                  <div className="mt-2 grid grid-cols-6 sm:grid-cols-8 gap-2 max-h-[264px] overflow-y-auto">
                    {RETAINER_STICKERS.filter((st) => st.category === stickerCat).map((st) => {
                      const picked = stickers.includes(st.id)
                      const full = stickers.length >= STICKER_SLOTS && !picked
                      return (
                        <button
                          key={st.id}
                          type="button"
                          disabled={full}
                          onClick={() => toggleSticker(st.id)}
                          title={st.name}
                          aria-label={st.name}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 bg-white p-1 transition ${picked ? 'border-brand-500 ring-2 ring-brand-200' : 'border-slate-200 hover:border-slate-400'} ${full ? 'opacity-35 cursor-not-allowed' : ''}`}
                        >
                          <img src={stickerImg(st.id)} alt="" loading="lazy" className="w-full h-full object-contain" />
                          {picked && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-brand-600 text-white flex items-center justify-center"><Check size={11} strokeWidth={3} /></span>}
                        </button>
                      )
                    })}
                  </div>
                </Field>
              )}

              {/* note */}
              <Field label="โน้ตถึง Lab">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="รายละเอียดเพิ่มเติม เช่น กลิตเตอร์ ตำแหน่งลวด ฯลฯ" />
              </Field>
            </>
          )}

          {err && <InlineError>{err}</InlineError>}
        </div>
    </Drawer>
  )
}

function summarize({ pt, upper, lower, type, usePreset, preset, pattern, colors, stickers, isRetainer }) {
  if (!isRetainer) return 'ดี-aligner'
  const ul = [upper && 'บน', lower && 'ล่าง'].filter(Boolean).join('+')
  if (type === 'Clear') return `Clear · ${ul}`
  if (usePreset) return `${ul} · ${type} · Preset: ${preset || '—'}`
  return `${ul} · ${type} · ${pattern || 'ยังไม่เลือกลาย'}${colors.length ? ' · ' + colors.join(', ') : ''}${stickers.length ? ` · สติกเกอร์ ${stickers.length}` : ''}`
}

// A numbered step heading (1 · ประเภทงาน …) — the kit <Field> has no step number, so
// this stays local (kit gap).
function Field({ label, step, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        {step != null && <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-[12px] font-semibold flex items-center justify-center shrink-0">{step}</span>}
        <span className="text-[13px] font-medium text-slate-700">{label}</span>
        {hint && <span className="text-[12px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </div>
  )
}
// หุ้มลวด picks from the same 19-colour set as the plate. A native <select> can't show the
// retainer photos, so this is a button that expands an inline grid — inline rather than a
// popover so it can't be clipped by the modal's own scroll container.
function RubberRow({ label, on, onToggle, color, onColor }) {
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(null)
  const hit = colorByName(color)
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
      <div className="flex items-center gap-2.5">
        <ChoiceChips multiple options={[{ value: 'on', label }]} values={on ? ['on'] : []} onChange={onToggle} />
        {on && (
          <button type="button" onClick={() => setOpen((v) => !v)}
            className="flex-1 min-w-0 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1 text-[13px] text-slate-700 hover:border-brand-300">
            {hit
              ? <img src={colorImg(hit.slug)} alt="" className="w-6 h-6 object-contain shrink-0" />
              : <span className="w-6 h-6 rounded-full border border-dashed border-slate-300 shrink-0" />}
            <span className={`flex-1 text-left truncate ${hit || color ? '' : 'text-slate-400'}`}>
              {hit ? `${hit.code} · ${hit.name}` : (color || 'เลือกสีหุ้มลวด…')}
            </span>
            <ChevronDown size={14} className={`shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {on && open && (
        <div className="mt-2">
          <ColorPreview color={hover} fallback="ชี้ที่สีเพื่อดูชื่อสี" />
          <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
            {RETAINER_COLORS.map((c) => (
              <ColorTile key={c.code} color={c} selected={color === c.name}
                onClick={() => { onColor(c.name); setOpen(false) }} onHover={setHover} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
// A swatch is only ~48px and several retainer colours read almost identically at that size
// (ฟ้าโทแพซ vs ขาวหิมะ, แดงกุหลาบ vs แดงป๊อปปี้), so the tile reports what it is on hover and
// ColorPreview names it. A per-tile tooltip would be clipped: the modal body scrolls on Y,
// which forces overflow-x to auto, so anything centred over the edge columns gets cut off.
function ColorTile({ color, selected, disabled, onClick, onHover }) {
  const report = (v) => onHover && onHover(v)
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => report(color)}
      onMouseLeave={() => report(null)}
      onFocus={() => report(color)}
      onBlur={() => report(null)}
      title={`${color.code} · ${color.name}`}
      aria-label={`${color.code} ${color.name}`}
      className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 bg-white transition ${selected ? 'border-brand-500 ring-2 ring-brand-200' : 'border-slate-200 hover:border-slate-400'} ${disabled ? 'opacity-35 cursor-not-allowed' : ''}`}
    >
      <img src={colorImg(color.slug)} alt="" loading="lazy" className="w-full h-full object-contain p-0.5" />
      {selected && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-brand-600 text-white flex items-center justify-center"><Check size={11} strokeWidth={3} /></span>}
    </button>
  )
}
// Fixed spot above a swatch grid that names whatever is under the cursor — bigger than the
// tile, so a colour can actually be read before it is picked.
function ColorPreview({ color, fallback }) {
  return (
    <div className="mb-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 min-h-[46px]">
      {color ? (
        <>
          <img src={colorImg(color.slug)} alt="" className="w-9 h-9 object-contain shrink-0" />
          <span className="text-[13.5px] font-medium text-slate-800 truncate">{color.name}</span>
          <span className="text-[12px] text-slate-400 shrink-0">{color.code}</span>
          <span className="ml-auto flex items-center gap-1 text-[11.5px] text-slate-400 shrink-0">
            <span className="w-3 h-3 rounded-full border border-slate-300" style={{ background: color.hex }} />
            {color.hex}
          </span>
        </>
      ) : (
        <span className="text-[12.5px] text-slate-400">{fallback}</span>
      )}
    </div>
  )
}
function ErrLine({ children }) { return <div className="mt-1 text-[12px] text-rose-600">{children}</div> }
function Note({ children }) { return <div className="mt-1.5 text-[12px] text-slate-500">{children}</div> }
